import moment from 'moment-timezone';

/**
 * Preset identifiers for the date range selector.
 * Regular presets compute a range; custom presets open dialogs for user input.
 */
export type DateRangePresetId =
  | 'today'
  | 'yesterday'
  | 'lastHour'
  | 'last24Hours'
  | 'last7Days'
  | 'last14Days'
  | 'last30Days'
  | 'last90Days'
  | 'last180Days'
  | 'lastWeek'
  | 'lastMonth'
  | 'thisWeek'
  | 'thisMonth'
  | 'yearToDate'
  | 'fromCustomDateUntilNow'
  | 'customFixedDateRange';

/** Presets that open a dialog instead of computing a range immediately. */
export const CUSTOM_PRESET_IDS: DateRangePresetId[] = [
  'fromCustomDateUntilNow',
  'customFixedDateRange',
];

export function isCustomPreset(id: DateRangePresetId): boolean {
  return CUSTOM_PRESET_IDS.includes(id);
}

export interface DateRangeValue {
  from: Date;
  to: Date;
}

/**
 * Computes the date range for a given preset in the specified timezone.
 * Uses moment-timezone for DST-safe start/end of day, week, month, year.
 * Does not handle custom presets (fromCustomDateUntilNow, customFixedDateRange);
 * callers must use dialog results for those.
 */
export function getPresetRange(
  presetId: DateRangePresetId,
  timezone: string,
  now?: Date,
): DateRangeValue | null {
  if (isCustomPreset(presetId)) {
    return null;
  }

  const ref = now ? moment(now).tz(timezone) : moment().tz(timezone);

  switch (presetId) {
    case 'today': {
      const from = ref.clone().startOf('day').toDate();
      const to = ref.clone().endOf('day').toDate();
      return { from, to };
    }

    case 'yesterday': {
      const start = ref.clone().subtract(1, 'day').startOf('day');
      const from = start.toDate();
      const to = start.clone().endOf('day').toDate();
      return { from, to };
    }

    case 'lastHour': {
      const to = ref.toDate();
      const from = ref.clone().subtract(1, 'hour').toDate();
      return { from, to };
    }

    case 'last24Hours': {
      const to = ref.toDate();
      const from = ref.clone().subtract(24, 'hours').toDate();
      return { from, to };
    }

    case 'last7Days': {
      const to = ref.clone().endOf('day').toDate();
      const from = ref.clone().subtract(6, 'days').startOf('day').toDate();
      return { from, to };
    }

    case 'last14Days': {
      const to = ref.clone().endOf('day').toDate();
      const from = ref.clone().subtract(13, 'days').startOf('day').toDate();
      return { from, to };
    }

    case 'last30Days': {
      const to = ref.clone().endOf('day').toDate();
      const from = ref.clone().subtract(29, 'days').startOf('day').toDate();
      return { from, to };
    }

    case 'last90Days': {
      const to = ref.clone().endOf('day').toDate();
      const from = ref.clone().subtract(89, 'days').startOf('day').toDate();
      return { from, to };
    }

    case 'last180Days': {
      const to = ref.clone().endOf('day').toDate();
      const from = ref.clone().subtract(179, 'days').startOf('day').toDate();
      return { from, to };
    }

    case 'lastWeek': {
      const start = ref.clone().subtract(1, 'week').startOf('week');
      const from = start.toDate();
      const to = start.clone().endOf('week').toDate();
      return { from, to };
    }

    case 'lastMonth': {
      const start = ref.clone().subtract(1, 'month').startOf('month');
      const from = start.toDate();
      const to = start.clone().endOf('month').toDate();
      return { from, to };
    }

    case 'thisWeek': {
      const from = ref.clone().startOf('week').toDate();
      const to = ref.clone().endOf('week').toDate();
      return { from, to };
    }

    case 'thisMonth': {
      const from = ref.clone().startOf('month').toDate();
      const to = ref.clone().endOf('month').toDate();
      return { from, to };
    }

    case 'yearToDate': {
      const from = ref.clone().startOf('year').toDate();
      const to = ref.clone().endOf('day').toDate();
      return { from, to };
    }

    default:
      return null;
  }
}

/** Ordered list of preset ids for the preset menu (excluding custom; those are appended). */
export const PRESET_MENU_ORDER: DateRangePresetId[] = [
  'today',
  'yesterday',
  'lastHour',
  'last24Hours',
  'last7Days',
  'last14Days',
  'last30Days',
  'last90Days',
  'last180Days',
  'lastWeek',
  'lastMonth',
  'thisWeek',
  'thisMonth',
  'yearToDate',
  'fromCustomDateUntilNow',
  'customFixedDateRange',
];
